import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import { useMediaQuery } from "react-responsive";
import { EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import responsive from "@/lib/static/responsive";
import { getVideoHostUrl } from "@/lib/utils";
import { useDotButton } from "../CarouselDotButton";

const carouselElements = [
  {
    title: "Learner Outcomes",
    description:
      "The framework is built around our five XQ Learner Outcomes. The XQ Learner Outcomes provide a north star for the teaching and learning all young people need.",
    src: `${getVideoHostUrl()}/2024/10/homepage1_LearnerOutcomes.mp4`,
    poster: "/assets/homepage/homepage1_LearnerOutcomes.png",
  },
  {
    title: "Competencies",
    description:
      "Derived from the XQ Learner Outcomes, the XQ Competencies define the full range of cognitive skills and social-emotional capacities that high school graduates need to thrive in college, career, and life.",
    src: `${getVideoHostUrl()}/2024/10/homepage2_Competencies.mp4`,
    poster: "/assets/homepage/homepage2_Competencies.png",
  },
  {
    title: "Component Skills",
    description:
      "Each competency is broken down into component skills and detailed, four-level progressions of student growth and development.",
    src: `${getVideoHostUrl()}/2024/10/homepage3_ComponentSkills.mp4`,
    poster: "/assets/homepage/homepage3_ComponentSkills.png",
  },
];

export const FrameworkCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const { selectedIndex, onDotButtonClick } = useDotButton(emblaApi, {
    shouldJump: true,
  });
  const [viewedSlides, setViewedSlides] = useState<number[]>([]);
  const [currentProgress, setCurrentProgress] = useState(0);

  const updateViewedSlides = useCallback((emblaApi: EmblaCarouselType) => {
    setViewedSlides((prevViewed) => {
      if (prevViewed.length === emblaApi.slideNodes().length) {
        emblaApi.off("slidesInView", updateViewedSlides);
        return prevViewed;
      }
      return emblaApi.slidesInView();
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    updateViewedSlides(emblaApi);
    emblaApi.on("slidesInView", updateViewedSlides);
    emblaApi.on("reInit", updateViewedSlides);
  }, [emblaApi, updateViewedSlides]);

  const advanceSlide = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext(true);
  }, [emblaApi]);

  const handleVideoEnd = useCallback(() => {
    setCurrentProgress(0);
    advanceSlide();
  }, [advanceSlide, setCurrentProgress]);

  const handleTimeUpdate = useCallback(
    (e: SyntheticEvent<HTMLVideoElement>) => {
      // Duration is NotaNumber until video loads.
      if (isNaN(e.currentTarget.duration)) {
        setCurrentProgress(0);
        return;
      }

      setCurrentProgress(
        (e.currentTarget.currentTime / e.currentTarget.duration) * 100,
      );
    },
    [setCurrentProgress],
  );

  const handleButtonClick = useCallback(
    (index: number) => () => {
      setCurrentProgress(0);
      onDotButtonClick(index);
    },
    [setCurrentProgress, onDotButtonClick],
  );

  return (
    <div className="embla medium:grid medium:grid-cols-2 medium:gap-4 large:gap-6">
      <div
        className="embla__viewport overflow-hidden rounded-[40px] border border-tab-border shadow-[0_20px_30px_-20px_rgba(57,43,21,0.25)]"
        ref={emblaRef}
      >
        <div className="embla__container grid auto-cols-[100%] grid-flow-col">
          {carouselElements.map(({ src, poster }, index) => (
            <LazyLoadedCarouselVideo
              key={`hp-carousel-video-${index}`}
              src={src}
              poster={poster}
              shouldLoadVideo={viewedSlides.includes(index)}
              isCurrentSlide={selectedIndex === index}
              handleVideoEnd={handleVideoEnd}
              handleTimeUpdate={handleTimeUpdate}
            />
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-2 small:mt-10 medium:mt-0 medium:flex medium:flex-col medium:justify-center medium:gap-0">
        {carouselElements.map(({ title, description }, index) => (
          <CarouselControlButton
            key={`hp-carousel-button-${index}`}
            title={title}
            description={description}
            onClick={handleButtonClick(index)}
            isActive={selectedIndex === index}
            currentProgress={currentProgress}
          />
        ))}
      </div>
      <p className="mt-6 text-sm leading-tight medium:hidden">
        {carouselElements[selectedIndex].description}
      </p>
    </div>
  );
};

type CarouselButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  title: string;
  description: string;
  isActive: boolean;
  currentProgress: number;
};

const CarouselControlButton = ({
  title,
  description,
  isActive,
  currentProgress,
  ...rest
}: CarouselButtonProps) => {
  return (
    <button
      className={`relative pt-4 text-center medium:my-6 medium:ml-10 medium:pl-4 medium:pt-0 medium:text-left ${
        isActive
          ? "border-t-2 border-BG-55 pt-[14px] small:border-[#d1d1d1] medium:border-l-2 medium:border-t-0 medium:pl-[14px]"
          : "text-[#B0B0B0] hover:text-black"
      }`}
      aria-label="carousel dot"
      {...rest}
    >
      <span className="block font-teodor text-lg leading-tight small:text-xl medium:text-[32px] medium:tracking-[-1px]">
        {title}
      </span>
      <span className="hidden text-sm leading-tight medium:mt-6 medium:block">
        {description}
      </span>
      {isActive && (
        <>
          <div
            className="absolute left-0 top-[-2px] hidden border-t-2 border-BG-55 transition-width duration-300 ease-linear small:block medium:hidden"
            style={{ width: `${currentProgress}%` }}
          />
          <div
            className="absolute left-[-2px] top-0 hidden border-l-2 border-BG-55 transition-height duration-300 ease-linear medium:block"
            style={{ height: `${currentProgress}%` }}
          />
        </>
      )}
    </button>
  );
};

type CarouselVideoProps = {
  src: string;
  poster: string;
  shouldLoadVideo: boolean;
  isCurrentSlide: boolean;
  handleVideoEnd: (e: SyntheticEvent<HTMLVideoElement>) => void;
  handleTimeUpdate: (e: SyntheticEvent<HTMLVideoElement>) => void;
};

export const LazyLoadedCarouselVideo = ({
  src,
  poster,
  shouldLoadVideo,
  isCurrentSlide,
  handleVideoEnd,
  handleTimeUpdate,
}: CarouselVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>();
  const { ref: inViewRef, inView } = useInView({ threshold: 0.6 });

  const isSmallOrLarger = useMediaQuery({
    minWidth: responsive.small,
  });

  const setRefs = useCallback(
    (node: HTMLVideoElement) => {
      videoRef.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  useEffect(() => {
    if (!videoRef.current) return;

    if (isCurrentSlide && inView) {
      videoRef.current.play();
    } else if (isCurrentSlide && !inView) {
      videoRef.current.pause();
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isCurrentSlide, inView, isSmallOrLarger]);

  return (
    <div className="embla__slide">
      {isSmallOrLarger ? (
        <video
          ref={setRefs}
          width={566}
          height={566}
          muted
          playsInline
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleVideoEnd}
          preload="auto"
        >
          {shouldLoadVideo && <source src={src} type="video/mp4" />}
        </video>
      ) : (
        <Image src={poster} alt="" height={566} width={566} />
      )}
    </div>
  );
};
